import { BaseInformation } from '@/models/games/BaseInformation';
import { GameState } from '@/models/games/enums/GameState';
import { SupportedGameResult } from '@/modules/host/models';
import {
    GameInitPayload,
    StartBetPayload,
    UpdateCountDownPayload,
    UpdateGameCountPayload,
    UpdateGameRestPayload,
    UpdateGameResultObjectPayload,
    UpdateGameResultPayload,
    UpdateGameStateByHostIdPayload,
    UpdateGoodRoadPayload,
    UpdatePokerShowPayload,
} from '@/modules/host/models/payloads';
import { getIsRestState, updateRemainTime } from '@/utils/commonFunc';
import { EntityState, PayloadAction } from '@reduxjs/toolkit';
import { hostLiveAdapter } from '.';
import { HostTableLive } from '../models';

const updateGameState = (
    state: EntityState<HostTableLive, number>,
    action: PayloadAction<UpdateGameStateByHostIdPayload>
) => {
    return hostLiveAdapter.updateOne(state, {
        id: action.payload.hostId,
        changes: {
            CurrentState: action.payload.state,
            ResultReleased: false,
        },
    });
};
const updateGoodRoad = (
    state: EntityState<HostTableLive, number>,
    action: PayloadAction<UpdateGoodRoadPayload>
) => {
    const host = state.entities[action.payload.hostId];
    if (host) {
        const goodRoad = action.payload.goodRoad;
        return hostLiveAdapter.updateOne(state, {
            id: host.HostId,
            changes: {
                GoodRoad: goodRoad,
                IsGoodRoad: goodRoad && Object.values(goodRoad).includes(true),
            },
        });
    }
};
const updateGameCount = (
    state: EntityState<HostTableLive, number>,
    action: PayloadAction<UpdateGameCountPayload>
) => {
    const host = state.entities[action.payload.hostId];
    let result =
        host && host.CurrentResult
            ? { ...host.CurrentResult, GameCount: action.payload.gameCount }
            : undefined;
    hostLiveAdapter.updateOne(state, {
        id: action.payload.hostId,
        changes: {
            CurrentResult: result,
        },
    });
};
const updateGameResultObject = (
    state: EntityState<HostTableLive, number>,
    action: PayloadAction<UpdateGameResultObjectPayload>
) =>
    hostLiveAdapter.updateOne(state, {
        id: action.payload.hostId,
        changes: {
            CurrentResult: action.payload.result,
        },
    });
const updateGameResult = (
    state: EntityState<HostTableLive, number>,
    action: PayloadAction<UpdateGameResultPayload>
) => {
    const host = state.entities[action.payload.hostId];
    if (host) {
        const resultReleased =
            action.payload.resultReleased === undefined
                ? true //default true;
                : action.payload.resultReleased;
        const currentResult: SupportedGameResult =
            host.CurrentResult ?? ({} as SupportedGameResult);
        currentResult.ResultString = action.payload.resultString;
        currentResult.Result = action.payload.result;
        currentResult.FResult = action.payload.result.toString();
        return hostLiveAdapter.updateOne(state, {
            id: host.HostId,
            changes: {
                CurrentResult: currentResult,
                ResultReleased: resultReleased,
                ShuffleTimer: action.payload.shuffleTimer,
            },
        });
    }
};
const gameInit = (
    state: EntityState<HostTableLive, number>,
    action: PayloadAction<GameInitPayload>
) => {
    const game = action.payload.game;
    const live = action.payload.liveData;
    const goodRoad = live.GoodRoad;
    return hostLiveAdapter.updateOne(state, {
        id: action.payload.hostId,
        changes: {
            CurrentResult: live.CurrentResult,
            CurrentState: live.CurrentState,
            GoodRoad: goodRoad,
            IsGoodRoad:
                !game.IsRest &&
                goodRoad &&
                Object.values(goodRoad).includes(true),
        },
    });
};
const updatePokerShow = (
    state: EntityState<HostTableLive, number>,
    action: PayloadAction<UpdatePokerShowPayload>
) =>
    hostLiveAdapter.updateOne(state, {
        id: action.payload.hostId,
        changes: {
            PokerShowResult: action.payload.results,
        },
    });
const startBet = (
    state: EntityState<HostTableLive, number>,
    action: PayloadAction<StartBetPayload>
) => {
    const hostId = action.payload.hostId;
    const host = state.entities[hostId];
    if (host) {
        return hostLiveAdapter.updateOne(state, {
            id: hostId,
            changes: {
                RemainTime: action.payload.remainTime,
                CurrentResult: action.payload.result,
                ResultReleased: false,
                CurrentState: GameState.Betting,
                CountDown: action.payload.countDown
                    ? action.payload.countDown
                    : host.CountDown,
                PendingTime: action.payload.pendingTime
                    ? action.payload.pendingTime
                    : 0,
                CanLeave: action.payload.canLeave === true,
            },
        });
    }
};
const updateCountDown = (
    state: EntityState<HostTableLive, number>,
    action: PayloadAction<UpdateCountDownPayload>
) => {
    hostLiveAdapter.updateOne(state, {
        id: action.payload.hostId,
        changes: {
            RemainTime: updateRemainTime(action.payload.countDown / 1000),
            CountDown: action.payload.countDown,
            PendingTime: 0,
            CanLeave: action.payload.canLeave,
        },
    });
};
const updateInformation = (
    state: EntityState<HostTableLive, number>,
    action: PayloadAction<BaseInformation>
) => {
    hostLiveAdapter.updateOne(state, {
        id: action.payload.HostId,
        changes: {
            CountDown: action.payload.CountDown,
            HostId: action.payload.HostId,
            RemainTime: action.payload.RemainTime,
        },
    });
};
const updateGameRest = (
    state: EntityState<HostTableLive, number>,
    action: PayloadAction<UpdateGameRestPayload>
) => {
    const isRest = getIsRestState(
        action.payload.onOrOff,
        action.payload.isTest
    );
    if (isRest) {
        let changes: Partial<HostTableLive> = {
            CurrentState: GameState.Idle,
        };
        return hostLiveAdapter.updateOne(state, {
            id: action.payload.hostId,
            changes,
        });
    }
};
export const hostLiveReducers = {
    gameInit,
    updateInformation,
    updateCountDown,
    startBet,
    updateGameState,
    updatePokerShow,
    updateGoodRoad,
    updateGameCount,
    updateGameResult,
    updateGameResultObject,
    updateGameRest,
};
